import React, { useState, useRef, useEffect } from 'react';

const educationAliases = {
  "Doctoral or professional degree": "Doctoral",
  "Master's degree": "Master's",
  "Bachelor's degree": "Bachelor's",
  "Associate's degree": "Associate",
  "Postsecondary nondegree award": "Nondegree",
  "Some college, no degree": "Some college",
  "High school diploma or equivalent": "High School",
  "No formal educational credential": "None",
  "NA": "NA",
};

const educationOptions = [
  'No formal educational credential',
  'High school diploma or equivalent',
  'Some college, no degree',
  'Postsecondary nondegree award',
  "Associate's degree",
  "Bachelor's degree",
  "Master's degree",
  'Doctoral or professional degree',
  'NA',
];

export default function EducationFilter({ education, setEducation }) {
  // Track whether the user is dragging and whether we’re in “select” or “unselect” mode.
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [dragMode, setDragMode] = useState(null); // 'select' or 'unselect' or null
  
  // A ref to the container so we can handle pointer events there if desired
  const containerRef = useRef(null);

  // Handle toggling logic
  const toggleOne = (ed) => {
    if (education.includes(ed)) {
      setEducation(education.filter((item) => item !== ed));
    } else {
      setEducation([...education, ed]);
    }
  };

  // A helper to *explicitly* select or unselect without toggling
  const setOne = (ed, shouldSelect) => {
    const isSelected = education.includes(ed);
    if (shouldSelect && !isSelected) {
      setEducation([...education, ed]);
    }
    if (!shouldSelect && isSelected) {
      setEducation(education.filter((item) => item !== ed));
    }
  };

  const handlePointerDown = (ed, e) => {
    e.preventDefault();
    // If the item was previously selected, then we’re in unselect mode
    // Otherwise, we’re in select mode
    const wasSelected = education.includes(ed);
    setDragMode(wasSelected ? 'unselect' : 'select');
    setIsPointerDown(true);
    // Immediately set/unset this item
    setOne(ed, !wasSelected);
  };

  const handlePointerEnter = (ed, e) => {
    // If we’re dragging, set or unset based on dragMode
    if (isPointerDown && dragMode) {
      const shouldSelect = dragMode === 'select';
      setOne(ed, shouldSelect);
    }
  };

  const handlePointerUp = () => {
    setIsPointerDown(false);
    setDragMode(null);
  };

  // You can also attach pointer events to the container so that if the user
  // drags outside, we track pointer cancellation, etc.
  useEffect(() => {
    const handlePointerUpOutside = () => {
      if (isPointerDown) {
        setIsPointerDown(false);
        setDragMode(null);
      }
    };
    // We listen at the top-level (window) for pointerup in case
    // user releases outside the container
    window.addEventListener('pointerup', handlePointerUpOutside);
    return () => {
      window.removeEventListener('pointerup', handlePointerUpOutside);
    };
  }, [isPointerDown]);

  return (
    <div className="-mx-6" ref={containerRef}>
      <label className="block font-medium mb-1">Typical Education for Entry</label>
      <div className="grid grid-cols-3 gap-2">
        {educationOptions.map((ed) => {
          const isSelected = education.includes(ed);
          return (
            <button
              key={ed}
              type="button"
              aria-pressed={isSelected}
              // Use pointer events rather than just onClick
              onPointerDown={(e) => handlePointerDown(ed, e)}
              onPointerEnter={(e) => handlePointerEnter(ed, e)}
              onPointerUp={handlePointerUp}
              className={`px-2 py-3 max-h-14 transition-colors rounded-full focus:outline-none truncate border-2 ${
                isSelected
                  ? 'bg-zinc-200 text-zinc-800 border-zinc-500'
                  : 'bg-zinc-100 text-zinc-600 border-transparent hover:bg-zinc-200'
              }`}
            >
              {educationAliases[ed]}
            </button>
          );
        })}
      </div>
    </div>
  );
}